import {
  createSlice,
  createSelector,
  PayloadAction,
  Selector,
  Dictionary,
  current
} from '@reduxjs/toolkit';
import { SortModelItem } from 'ag-grid-community';
import {
  difference,
  differenceBy,
  groupBy,
  intersectionWith,
  union,
  unionBy
} from 'lodash';
import { REDUX_SLICE_KEYS, Tabs } from 'common/constants';
import { getCompanyId, getStoreId } from 'components/StoreSelect/slice';
import { getUpdatedInvoiceDescriptions } from 'pages/AuthorizePaymentPage/slice';
import {
  LockInvoiceSuccess,
  LockStatus
} from 'pages/AuthorizePaymentPage/types';
import { FilterDataType } from 'pages/HighVolumeLandingPage/types';
import { getSelectedTab } from 'pages/LandingPage/components/LandingPageHeader/slice';
import { getHarnessFlagValue } from 'pages/LandingPage/slice';
import { convertToDateFormat } from 'utils/ConvertDateFormat';
import { VENDORS_TABLE_FIELDS } from './constants';
import { getNumberFromCurrency, getVendorsTableRowData } from './helpers';
import type {
  VendorsTableStateType,
  SelectInvoicesPayload,
  VendorsResponseDataType,
  VendorsTableDataType,
  VendorsTableDataOptions,
  SetSelectedInvoiceDataPayload
} from './types';
import type { RootState } from 'store';

const initialState: VendorsTableStateType = {
  dueDate: '',
  paymentMethods: [],
  selectedInvoiceIds: [],
  selectedInvoiceData: [],
  vendorsResponse: null,
  successfullyLockedInvoices: [],
  searchInputValue: '',
  debouncedSearchInputValue: '',
  isLoadingVendors: false,
  showFullPageLoader: false,
  highVolumeFilterData: null,
  highVolumeColumnState: undefined,
  prevSortColumn: undefined,
  prevSortOrder: undefined
};

export const vendorsSlice = createSlice({
  name: REDUX_SLICE_KEYS.vendors,
  initialState,
  reducers: {
    setDueDate: (state, action: PayloadAction<string>): void => {
      state.dueDate = action.payload;
    },
    setIsLoadingVendors: (state, action: PayloadAction<boolean>): void => {
      state.isLoadingVendors = action.payload;
    },
    setPaymentMethods: (
      state,
      action: PayloadAction<
        ((state: VendorsTableStateType) => string[]) | string[]
      >
    ): void => {
      state.paymentMethods =
        typeof action.payload === 'function'
          ? action.payload(state)
          : action.payload;
    },
    setVendorsResponse: (
      state,
      action: PayloadAction<VendorsResponseDataType>
    ): void => {
      state.vendorsResponse = action.payload;
    },
    setPrevSortColumn: (
      state,
      action: PayloadAction<string | undefined>
    ): void => {
      state.prevSortColumn = action.payload;
    },
    setPrevSortOrder: (
      state,
      action: PayloadAction<string | undefined>
    ): void => {
      state.prevSortOrder = action.payload;
    },
    setSuccessfullyLockedInvoices: (
      state,
      action: PayloadAction<LockInvoiceSuccess[]>
    ): void => {
      state.successfullyLockedInvoices = action.payload;
    },
    selectInvoices: (
      state,
      action: PayloadAction<SelectInvoicesPayload>
    ): void => {
      if (action.payload.selected) {
        state.selectedInvoiceIds = union(
          current(state).selectedInvoiceIds,
          action.payload.invoiceIds
        );
      } else {
        state.selectedInvoiceIds = difference(
          current(state).selectedInvoiceIds,
          action.payload.invoiceIds
        );
      }
    },
    selectInvoiceData: (
      state,
      action: PayloadAction<SetSelectedInvoiceDataPayload>
    ): void => {
      if (action.payload.selected) {
        state.selectedInvoiceData = unionBy(
          current(state).selectedInvoiceData,
          action.payload.invoices,
          'id'
        );
      } else {
        state.selectedInvoiceData = differenceBy(
          current(state).selectedInvoiceData,
          action.payload.invoices,
          'id'
        );
      }
    },
    clearSelectedInvoices: (
      state,
      action: PayloadAction<
        Array<{ vendorNo: string; invoiceId: string }> | undefined
      >
    ): void => {
      state.selectedInvoiceIds = action.payload
        ? state.selectedInvoiceIds.filter(
            invoiceId =>
              !action.payload?.find(invoice => invoice.invoiceId === invoiceId)
          )
        : [];
      state.selectedInvoiceData = action.payload
        ? state.selectedInvoiceData.filter(
            ({ vendorNo, invoiceId }) =>
              !action.payload?.find(
                invoice =>
                  invoice.vendorNo === vendorNo &&
                  invoice.invoiceId === invoiceId
              )
          )
        : [];
    },
    setSelectAllSelectedInvoices: (
      state,
      action: PayloadAction<VendorsTableDataType[]>
    ): void => {
      state.selectedInvoiceData = action.payload;
    },
    setSearchInputValue: (state, { payload }: PayloadAction<string>): void => {
      state.searchInputValue = payload;
    },
    setDebouncedSearchInputValue: (
      state,
      action: PayloadAction<string>
    ): void => {
      state.debouncedSearchInputValue = action.payload;
    },
    setFullPageLoader: (state, action): void => {
      state.showFullPageLoader = action.payload;
    },
    setHighVolumeFilterData: (
      state,
      action: PayloadAction<FilterDataType>
    ): void => {
      state.highVolumeFilterData = action.payload;
    },
    setHighVolumeColumnState: (
      state,
      action: PayloadAction<SortModelItem | undefined>
    ): void => {
      state.highVolumeColumnState = action.payload;
    }
  }
});

export const {
  setDueDate,
  setPaymentMethods,
  setPrevSortColumn,
  setPrevSortOrder,
  setVendorsResponse,
  setSuccessfullyLockedInvoices,
  clearSelectedInvoices,
  setSelectAllSelectedInvoices,
  selectInvoices,
  selectInvoiceData,
  setDebouncedSearchInputValue,
  setSearchInputValue,
  setIsLoadingVendors,
  setFullPageLoader,
  setHighVolumeFilterData,
  setHighVolumeColumnState
} = vendorsSlice.actions;

export const getInitialState = (state: RootState): RootState => state;

export const getDueDate = createSelector(
  getInitialState,
  ({ vendors }) => vendors.dueDate
);

export const getIsLoadingVendors = createSelector(
  getInitialState,
  ({ vendors }) => vendors.isLoadingVendors
);

export const getDueDateInFormatToDisplay = createSelector(getDueDate, dueDate =>
  convertToDateFormat(dueDate)
);

export const getPaymentMethods = createSelector(
  getInitialState,
  ({ vendors }) => vendors.paymentMethods
);

export const getVendorsResponse = createSelector(
  getInitialState,
  ({ vendors }) => vendors.vendorsResponse
);

export const getPrevSortColumn = createSelector(
  getInitialState,
  ({ vendors }) => vendors.prevSortColumn
);

export const getPrevSortOrder = createSelector(
  getInitialState,
  ({ vendors }) => vendors.prevSortOrder
);

export const getVendorsDataQueryOptions = createSelector(
  [getDueDate, getPaymentMethods, getCompanyId, getStoreId],
  (dueDate, paymentMethods, companyId, storeId): VendorsTableDataOptions => ({
    dueDate,
    paymentMethod: paymentMethods.length === 1 ? paymentMethods[0] : undefined,
    companyId,
    storeId
  })
);

export const getSearchInputValue = createSelector(
  getInitialState,
  ({ vendors }): string => vendors.searchInputValue
);

export const getShowFullPageLoaderValue = createSelector(
  getInitialState,
  ({ vendors }): boolean => vendors.showFullPageLoader
);

export const getDebouncedSearchInputValue = createSelector(
  getInitialState,
  ({ vendors }): string => vendors.debouncedSearchInputValue
);

export const getRowData = createSelector(
  [getVendorsResponse, getDebouncedSearchInputValue],
  (vendorsResponse, debouncedSearchInputValue) =>
    getVendorsTableRowData(vendorsResponse, debouncedSearchInputValue)
);

export const getRowDataNoFilter = createSelector(
  [getVendorsResponse],
  vendorsResponse => getVendorsTableRowData(vendorsResponse, '')
);

export const getRowDataByVendor = createSelector(
  getRowData,
  (rowData): Dictionary<VendorsTableDataType[]> =>
    groupBy(rowData, VENDORS_TABLE_FIELDS.vendorNo)
);

export const getRowDataVendorsLength = createSelector(
  getRowDataByVendor,
  rowDataByVendor => Object.keys(rowDataByVendor).length
);

export const getTotalNoOfVendors = createSelector(
  getVendorsResponse,
  vendorsResponse => vendorsResponse?.totalNoOfVendors ?? 0
);

export const getSelectedInvoiceIds = createSelector(
  getInitialState,
  ({ vendors }) => vendors.selectedInvoiceIds
);

export const getSelectedInvoiceData = createSelector(
  getInitialState,
  ({ vendors }) => vendors.selectedInvoiceData
);

export const getSelectedInvoices = createSelector(
  [
    getSelectedInvoiceIds,
    getRowDataNoFilter,
    getSelectedInvoiceData,
    getHarnessFlagValue('dmsAccountingLandingPage'),
    getUpdatedInvoiceDescriptions
  ],
  (
    selectedInvoiceIds,
    rowDataNoFilter,
    selectedInvoiceData,
    landingPageLDFlag,
    updatedInvoiceDescriptions
  ) => {
    const rows = landingPageLDFlag
      ? selectedInvoiceData
      : intersectionWith(
          rowDataNoFilter,
          selectedInvoiceIds,
          (invoice, invoiceId) => invoice.id === invoiceId
        );
    return rows.map(row => ({
      ...row,
      invoiceDescription:
        updatedInvoiceDescriptions.find(
          ({ vendorNo, invoiceId }) =>
            row.vendorNo === vendorNo && row.invoiceId === invoiceId
        )?.description ?? row.invoiceDescription
    }));
  }
);

export const getSelectedInvoicesLength = createSelector(
  getSelectedInvoices,
  selectedInvoices => selectedInvoices.length
);

export const getSelectedVendorInvoicePairs = createSelector(
  getSelectedInvoices,
  selectedInvoices =>
    selectedInvoices.map(({ vendorNo, invoiceId }) => ({
      vendorNo,
      invoiceId
    }))
);

export const getSelectedInvoicesByVendor = createSelector(
  getSelectedInvoices,
  selectedInvoices => groupBy(selectedInvoices, VENDORS_TABLE_FIELDS.vendorNo)
);

export const getLockInvoicesPayload = createSelector(
  getSelectedInvoices,
  selectedInvoices =>
    selectedInvoices.map(({ vendorNo, invoiceId }) => ({
      vendorNo,
      invoiceId
    }))
);

export const getSuccessfullyLockedInvoices = createSelector(
  getInitialState,
  ({ vendors }) => vendors.successfullyLockedInvoices
);

export const getSuccessfullyLockedInvoicesByVendor = createSelector(
  [getSelectedInvoices, getSuccessfullyLockedInvoices],
  (selectedInvoices, successfullyLockedInvoices) => {
    const invoices = selectedInvoices.filter(({ vendorNo, invoiceId }) =>
      successfullyLockedInvoices.find(
        invoice =>
          invoice.vendorNo === vendorNo && invoice.invoiceId === invoiceId
      )
    );
    return groupBy(invoices, VENDORS_TABLE_FIELDS.vendorNo);
  }
);

export const getSuccessfullyLockedInvoicesLength = createSelector(
  [getSuccessfullyLockedInvoices],
  successfullyLockedInvoices => successfullyLockedInvoices.length
);

export const getPostPaymentApprovalsPayload = createSelector(
  [getSuccessfullyLockedInvoicesByVendor, getSelectedTab],
  (successfullyLockedInvoicesByVendor, selectedTab) =>
    Object.entries(successfullyLockedInvoicesByVendor).map(
      ([vendorNo, invoices]) => ({
        vendorId: vendorNo,
        paymentType: invoices[0].paymentMethod,
        isPrintApproved: selectedTab !== Tabs.ToBePaid,
        invoices: invoices.map(({ invoiceId, invoiceDescription }) => ({
          invoiceId,
          invoiceDescription
        }))
      })
    )
);

export const getAuthorizeApChecksPayload = createSelector(
  getSelectedInvoices,
  selectedInvoices =>
    selectedInvoices.map(({ vendorNo, invoiceId }) => ({ vendorNo, invoiceId }))
);

export const getSelectedVendorTotals = createSelector(
  getSelectedInvoicesByVendor,
  (selectedInvoicesByVendor): Dictionary<number> =>
    Object.entries(selectedInvoicesByVendor).reduce(
      (acc, [vendorNo, invoices]) => {
        const vendorTotal = invoices.reduce(
          (total, invoice) => total + getNumberFromCurrency(invoice.netAmount),
          0
        );
        return {
          ...acc,
          [vendorNo]: vendorTotal
        };
      },
      {}
    )
);

export const getSelectedVendorTotal = (
  vendorNo?: string
): Selector<RootState, number> =>
  createSelector(
    getSelectedVendorTotals,
    selectedVendorTotals => selectedVendorTotals[vendorNo || ''] || 0
  );

export const getVendorRowData = (
  key: string | null
): Selector<RootState, VendorsTableDataType[]> =>
  createSelector(
    getRowDataByVendor,
    rowDataByVendor => rowDataByVendor[key || ''] || []
  );

export const getIsRowSelected = (
  key: string | null,
  vendorNo?: string,
  invoiceId?: string
): Selector<RootState, boolean> =>
  createSelector(getSelectedInvoicesByVendor, selectedInvoicesByVendor => {
    if (key) return !!selectedInvoicesByVendor[key]?.length;
    if (vendorNo && invoiceId) {
      return !!selectedInvoicesByVendor[vendorNo]?.find(
        invoice => invoiceId === invoice.invoiceId
      );
    }
    return false;
  });

export const getIsRowIndeterminate = (
  key: string | null
): Selector<RootState, boolean> =>
  createSelector(
    [getSelectedInvoicesByVendor, getVendorRowData(key)],
    (selectedInvoicesByVendor, vendorRowData) =>
      key
        ? (selectedInvoicesByVendor[key]?.length || 0) < vendorRowData.length
        : false
  );

export const getSelectedRows = (
  key: string | null
): Selector<RootState, VendorsTableDataType[]> =>
  createSelector([getSelectedInvoicesByVendor], selectedInvoicesByVendor =>
    key ? selectedInvoicesByVendor[key] ?? [] : []
  );

export const getIsRowDisabled = (
  key: string | null,
  vendorNo?: string,
  invoiceId?: string
): Selector<RootState, boolean> =>
  createSelector(
    [
      getSelectedInvoicesByVendor,
      getRowDataByVendor,
      getSelectedVendorTotal(vendorNo),
      getSelectedTab
    ],
    (
      selectedInvoicesByVendor,
      rowDataByVendor,
      selectedVendorTotal,
      selectedTab
    ) => {
      if (key) {
        const vendorInvoices = rowDataByVendor[key] || [];
        return (
          vendorInvoices[0]?.vendorOnHold ||
          vendorInvoices.every(
            ({ lockStatus, netAmount, onHold }) =>
              onHold ||
              getNumberFromCurrency(netAmount) < 0 ||
              (selectedTab === Tabs.ToBePaid &&
                lockStatus === LockStatus.LOCKED)
          )
        );
      }
      if (vendorNo && invoiceId) {
        const vendorInvoices = rowDataByVendor[vendorNo] || [];
        const { lockStatus, netAmount, onHold, vendorOnHold } =
          vendorInvoices.find(invoice => invoiceId === invoice.invoiceId) || {};
        const isSelected = !!selectedInvoicesByVendor[vendorNo]?.find(
          invoice => invoiceId === invoice.invoiceId
        );
        const factor = isSelected ? -1 : 1;
        return (
          onHold ||
          vendorOnHold ||
          selectedVendorTotal + factor * getNumberFromCurrency(netAmount) < 0 ||
          (selectedTab === Tabs.ToBePaid && lockStatus === LockStatus.LOCKED)
        );
      }
      return false;
    }
  );

export const getSelectedTotal = createSelector(
  getSelectedInvoices,
  selectedInvoices =>
    selectedInvoices.reduce(
      (total, invoice) => total + getNumberFromCurrency(invoice.netAmount),
      0
    )
);

export const getSelectedTotalDiscount = createSelector(
  getSelectedInvoices,
  selectedInvoices =>
    selectedInvoices.reduce(
      (total, invoice) => total + getNumberFromCurrency(invoice.discountAmount),
      0
    )
);

export const getHighVolumeFilterData = createSelector(
  getInitialState,
  ({ vendors }): FilterDataType | null => vendors.highVolumeFilterData
);

export const getHighVolumeColumnState = createSelector(
  getInitialState,
  ({ vendors }): SortModelItem | undefined => vendors.highVolumeColumnState
);

export default vendorsSlice.reducer;
